import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Calendar } from 'lucide-react';
import { contentAPI } from '../utils/api';
import { openReservation } from '../utils/reservations';

// Module-level cache so the Navbar never refetches / flashes during the session.
let CACHED_NAV = null;
let CACHED_NAV_PROMISE = null;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [nav, setNav] = useState(CACHED_NAV);
  const navigate = useNavigate();

  useEffect(() => {
    if (CACHED_NAV) {
      setNav(CACHED_NAV);
      return;
    }
    if (!CACHED_NAV_PROMISE) {
      CACHED_NAV_PROMISE = contentAPI.getNav();
    }
    CACHED_NAV_PROMISE.then((d) => {
      CACHED_NAV = d || {};
      setNav(CACHED_NAV);
    });
  }, []);

  const handleNavClick = (link) => {
    setIsOpen(false);
    if (link.is_external) {
      window.open(link.path, '_blank');
    } else {
      navigate(link.path);
    }
  };

  const links = (nav?.links || [])
    .filter((l) => l.is_active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const logoUrl = nav?.logo_url || '/api/upload/images/b57dd0389093441f8ae04dcbc70eabef.jpg';
  const logoAlt = nav?.logo_alt || 'Brick Lane';
  const bgImage = nav?.background_image || 'https://images.unsplash.com/photo-1595514736518-25e7dff0037d?w=1920&q=80';
  const ctaLabel = nav?.cta_label;
  const ctaLink = nav?.cta_link;

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 shadow-lg"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="site-navbar"
    >
      <div className="absolute inset-0 bg-black/40"></div>
      <div className="max-w-7xl mx-auto px-6 py-4 relative z-10">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img src={logoUrl} alt={logoAlt} className="h-10 md:h-12 w-auto object-contain" />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {links.map((link, idx) => (
              <button
                key={idx}
                onClick={() => handleNavClick(link)}
                className="text-base font-medium hover:opacity-70 transition-opacity"
                style={{ color: link.path === '/diwali' ? '#E3C27E' : '#ffffff' }}
                data-testid={`navbar-link-${idx}`}
              >
                {link.label}
              </button>
            ))}
            {ctaLabel && ctaLink && (
              <button
                onClick={() => openReservation(ctaLink, 'navbar')}
                className="bg-white text-black px-5 py-2 text-sm font-semibold tracking-wide uppercase hover:bg-gray-100 transition-all inline-flex items-center gap-2"
                data-testid="navbar-cta-btn"
              >
                <Calendar size={16} /> {ctaLabel}
              </button>
            )}
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3" data-testid="navbar-mobile-menu">
            {links.map((link, idx) => (
              <button
                key={idx}
                onClick={() => handleNavClick(link)}
                className="block w-full text-left text-base font-medium hover:opacity-70 transition-opacity py-2"
                style={{ color: link.path === '/diwali' ? '#E3C27E' : '#ffffff' }}
              >
                {link.label}
              </button>
            ))}
            {ctaLabel && ctaLink && (
              <button
                onClick={() => openReservation(ctaLink, 'navbar-mobile')}
                className="w-full bg-white text-black px-5 py-3 text-sm font-semibold tracking-wide uppercase inline-flex items-center justify-center gap-2"
              >
                <Calendar size={16} /> {ctaLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
